import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp, Home, List } from 'lucide-react';
import { localePathPrefix } from '../../utils/localeRouting';

const STORAGE_KEY = 'search-explore-nav-bar-collapsed';

/**
 * Fixed strip above the chat bar: Lessons / Home. Shown on all viewports; on desktop
 * it starts past the left sidebar. Collapsible; sessionStorage for this session.
 */
export default function MobileExploreNavBar({ chatInputBottomPx, onReserveHeightChange }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const prefix = localePathPrefix(location.pathname);
  const topicsTo = `${prefix}/topics`;
  const homeTo = '/';

  const setCollapsedPersist = useCallback((next) => {
    setCollapsed(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!onReserveHeightChange) return;
    onReserveHeightChange(collapsed ? 42 : 54);
  }, [collapsed, onReserveHeightChange]);

  const bottom = `calc(${Math.max(0, chatInputBottomPx)}px + env(safe-area-inset-bottom, 0px))`;

  if (collapsed) {
    return (
      <>
        <div
          className="search-explore-nav-bar search-explore-nav-bar--collapsed"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom,
            zIndex: 9998,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
      >
        <button
          type="button"
          onClick={() => setCollapsedPersist(false)}
          style={{
            pointerEvents: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            borderRadius: 999,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-card)',
            color: 'var(--color-text)',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 -2px 12px var(--color-shadow)',
            cursor: 'pointer',
            marginBottom: 6,
          }}
          aria-expanded="false"
          aria-label="Show lessons and home navigation"
        >
          <List size={18} strokeWidth={2.2} aria-hidden />
          Lessons
          <ChevronUp size={18} strokeWidth={2.2} aria-hidden />
        </button>
        </div>
        <style>
          {`
            @media (min-width: 769px) {
              .search-explore-nav-bar.search-explore-nav-bar--collapsed {
                left: 80px !important;
              }
            }
          `}
        </style>
      </>
    );
  }

  return (
    <>
    <nav
      className="search-explore-nav-bar search-explore-nav-bar--expanded"
      aria-label="Lessons and home"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        paddingBottom: 10,
        background: 'var(--color-bg-card)',
        borderTop: '1px solid var(--color-border)',
        boxShadow: '0 -4px 16px var(--color-shadow)',
        boxSizing: 'border-box',
      }}
    >
      <Link
        to={topicsTo}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 12,
          background: 'var(--color-primary)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 15,
          textDecoration: 'none',
          minHeight: 44,
          boxSizing: 'border-box',
        }}
      >
        <List size={20} strokeWidth={2.2} aria-hidden />
        Lessons
      </Link>
      <Link
        to={homeTo}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 12,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          fontWeight: 600,
          fontSize: 15,
          textDecoration: 'none',
          minHeight: 44,
          boxSizing: 'border-box',
        }}
      >
        <Home size={20} strokeWidth={2.2} aria-hidden />
        Home
      </Link>
      <button
        type="button"
        onClick={() => setCollapsedPersist(true)}
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: 12,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          cursor: 'pointer',
        }}
        aria-label="Hide this bar"
      >
        <ChevronDown size={22} strokeWidth={2.2} aria-hidden />
      </button>
    </nav>
    <style>
      {`
        @media (min-width: 769px) {
          .search-explore-nav-bar.search-explore-nav-bar--expanded {
            left: 80px !important;
          }
        }
      `}
    </style>
    </>
  );
}
